import { defineStore } from 'pinia';
import { isRef, useId, type MaybeRef } from 'vue';
import { computed, ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';
import { onUnmounted } from 'vue';
import { watch } from 'vue';
import { unref } from 'vue';
import type { ComputedRef } from 'vue';

export type HighlightBgType =
  'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'neutral' | 'error' | null;

export const useHighlightBgStore = defineStore('highlightBg', () => {
  const highlightBgStack = ref<{ bg: HighlightBgType; priority: number; id: string }[]>([]);

  const highlightBgClass = computed(() => {
    const topHighlightBg = highlightBgStack.value[0]?.bg ?? null;

    switch (topHighlightBg) {
      case 'primary':
        return 'bg-primary/20';
      case 'secondary':
        return 'bg-secondary/20';
      case 'accent':
        return 'bg-accent/20';
      case 'info':
        return 'bg-info/20';
      case 'success':
        return 'bg-success/20';
      case 'warning':
        return 'bg-warning/20';
      case 'error':
        return 'bg-error/20';
      case 'neutral':
      default:
        return '';
    }
  });

  return {
    highlightBgStack,
    highlightBgClass,
  };
});

export const useHighlightBg = (
  color?: MaybeRef<HighlightBgType> | ComputedRef<HighlightBgType>,
) => {
  const store = usePiniaRefStore(useHighlightBgStore());
  const id = useId();

  const highlightBg = (bg: HighlightBgType, priority = 0) => {
    store.highlightBgStack.value = [
      ...store.highlightBgStack.value.filter((item) => item.id !== id),
      { bg, priority, id },
    ].sort((a, b) => b.priority - a.priority);
  };

  onUnmounted(() => {
    store.highlightBgStack.value = store.highlightBgStack.value.filter((item) => item.id !== id);
  });

  if (isRef(color)) {
    watch(color, (newColor) => highlightBg(newColor), { immediate: true });
  } else if (color) {
    highlightBg(unref(color));
  }

  return { ...store, highlightBg };
};
